import { Button } from '@/shared/ui/button'
import { ClipboardList, Smartphone, BookOpen, Heart, Download } from 'lucide-react'
import { useEffect, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import SecretAdminLogin from '@/features/admin/SecretAdminLogin'
import config from '@/app/config'
import { MIDWIFE_CODE_KEY, VOUCHER_CODE_KEY, writeStorage } from '@/shared/lib/storageKeys'

export default function LandingPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [overHero, setOverHero] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const refCode = searchParams.get('ref')
    if (refCode) {
      writeStorage(VOUCHER_CODE_KEY, refCode.toUpperCase())
    }
    const midwifeCode = searchParams.get('midwife')
    if (midwifeCode) {
      writeStorage(MIDWIFE_CODE_KEY, midwifeCode)
    }
  }, [searchParams])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const onScroll = () => setOverHero(container.scrollTop < window.innerHeight * 0.85)
    container.addEventListener('scroll', onScroll, { passive: true })
    return () => container.removeEventListener('scroll', onScroll)
  }, [])

  const visitSignInUser = async () => {
    navigate('/sign-in/email-password')
  }

  const visitSignUpUser = async () => {
    navigate('/sign-up/email-password')
  }

  return (
    <div ref={containerRef} className="h-screen overflow-y-auto bg-white text-secondary">
      {/* Header */}
      <header
        className="fixed top-0 left-0 right-0 z-50 mt-safe px-2 py-2 md:px-6 md:py-4 flex justify-between items-center"
        style={{
          background:
            'linear-gradient(to bottom, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.15) 60%, transparent 100%)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
      >
        <div
          className="text-lg md:text-2xl transition-colors duration-300"
          style={{
            color: overHero ? 'white' : undefined,
            fontFamily: 'Georgia, "Times New Roman", serif',
            ...(overHero ? {} : { color: 'var(--primary)' }),
          }}
        >
          Institut für Stillberatung
        </div>
        <div className="space-x-4">
          <Button
            className="px-4 py-2 border rounded-lg font-medium text-primary bg-background hover:bg-white hover:text-primary"
            onClick={visitSignInUser}
          >
            Sign in
          </Button>
          <Button
            className="px-4 py-2 bg-primary rounded-lg font-medium hover:bg-primary-foreground"
            onClick={visitSignUpUser}
          >
            Sign up
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative w-full h-screen overflow-hidden">
        <img
          src="/images/mutter3.jpg"
          alt="Digitale Stillberatung"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Dunkler Overlay für Lesbarkeit */}
        <div className="absolute inset-0 bg-black/30" />
        {/* Text mittig über dem Bild */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <h1
            className="text-4xl md:text-6xl font-light tracking-wide text-white mb-4"
            style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
          >
            Eine friedliche Stillbeziehung
          </h1>
          <p
            className="text-lg md:text-xl text-white/85 font-light tracking-widest uppercase mb-10"
            style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
          >
            evidenzbasiert und einfühlsam
          </p>
          <Button
            onClick={visitSignInUser}
            className="px-8 py-3 bg-background hover:bg-white text-primary text-base rounded-full font-semibold shadow-lg tracking-wide"
          >
            Jetzt kostenlos starten
          </Button>
        </div>
      </section>

      {/* Drei Schritte */}
      <section className="px-6 py-24 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-center w-20 h-20 rounded-full bg-accent mb-6 mx-auto">
            <img src="/ifs_transparent.png" alt="Logo" className="h-12" />
          </div>
          <h2 className="text-3xl text-primary font-bold text-center mb-4">So funktioniert es</h2>
          <p className="text-center text-secondary mb-16 text-lg max-w-2xl mx-auto">
            In drei einfachen Schritten zu deiner individuellen Stillvorbereitung.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Schritt 1 */}
            <div className="relative flex flex-col items-center text-center p-8 rounded-2xl bg-background hover:bg-accent transition shadow-sm">
              <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center mb-5 shadow-md">
                <ClipboardList className="w-6 h-6 text-white" />
              </div>
              <span className="text-xs font-semibold tracking-widest text-muted-foreground uppercase mb-2">
                Schritt 1
              </span>
              <h3 className="text-xl font-bold text-primary mb-3">Fragebogen ausfüllen</h3>
              <p className="text-secondary leading-relaxed">
                Beantworte einige gezielte Fragen zu deiner aktuellen Situation. So können wir deine
                Bedürfnisse besser verstehen und die Inhalte optimal auf dich abstimmen.
              </p>
            </div>

            {/* Schritt 2 */}
            <div className="relative flex flex-col items-center text-center p-8 rounded-2xl bg-background hover:bg-accent transition shadow-sm">
              <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center mb-5 shadow-md">
                <Smartphone className="w-6 h-6 text-white" />
              </div>
              <span className="text-xs font-semibold tracking-widest text-muted-foreground uppercase mb-2">
                Schritt 2
              </span>
              <h3 className="text-xl font-bold text-primary mb-3">Breast Check</h3>
              <p className="text-secondary leading-relaxed">
                Mit unserem Breast Check erhältst du eine erste Einschätzung, ob physiologisches
                Stillen bei dir möglich ist – einfach, verständlich und bequem von zu Hause aus.
              </p>
            </div>

            {/* Schritt 3 */}
            <div className="relative flex flex-col items-center text-center p-8 rounded-2xl bg-background hover:bg-accent transition shadow-sm">
              <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center mb-5 shadow-md">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <span className="text-xs font-semibold tracking-widest text-muted-foreground uppercase mb-2">
                Schritt 3
              </span>
              <h3 className="text-xl font-bold text-primary mb-3">
                Dein personalisierter Stillvorbereitungskurs
              </h3>
              <p className="text-secondary leading-relaxed">
                Auf Basis deiner Angaben und Ergebnisse bekommst du einen individuell auf dich
                zugeschnittenen Kurs, der dich optimal auf deine Stillzeit vorbereitet.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 text-center">
            <p className="text-lg text-primary font-semibold mb-6">
              Starte jetzt – für einen sicheren und entspannten Stillbeginn.
            </p>
            <Button
              onClick={visitSignUpUser}
              className="px-8 py-3 bg-primary text-white text-base rounded-full font-semibold hover:bg-primary/90 shadow-md"
            >
              Jetzt kostenlos starten
            </Button>

            {/* App Store Links - Mobile */}
            <div className="mt-8 flex flex-col items-center gap-3 md:hidden">
              <p className="text-muted-foreground text-sm">Auch als App verfügbar</p>
              <div className="flex gap-3">
                <a
                  href="https://play.google.com/store/apps/details?id=de.stillberatung.institut"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-secondary hover:bg-secondary/80 rounded-lg text-white text-sm"
                >
                  <Download className="w-4 h-4" />
                  Google Play
                </a>
                <a
                  href="https://apps.apple.com/app/institut-fuer-stillberatung/id0000000000"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-secondary hover:bg-secondary/80 rounded-lg text-white text-sm"
                >
                  <Download className="w-4 h-4" />
                  App Store
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expertenteam */}
      <section className="px-6 py-24 bg-background">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl text-primary font-bold text-center mb-4">
            Unser Expertinnenteam für deine Stillvorbereitung
          </h2>
          <p className="text-center text-secondary text-lg max-w-2xl mx-auto mb-16 leading-relaxed">
            Hinter unserem Angebot steht ein interdisziplinäres Team aus erfahrenen Expertinnen, die
            dich einfühlsam und wissenschaftlich fundiert auf deine Stillzeit vorbereiten.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="flex flex-col items-center text-center bg-white rounded-2xl p-8 shadow-sm">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-5 text-3xl">
                👩‍⚕️
              </div>
              <h3 className="text-lg font-bold text-primary mb-1">Katrin Bautsch</h3>
              <p className="text-sm font-semibold text-muted-foreground tracking-wide uppercase mb-3">
                IBCLC
              </p>
              <p className="text-secondary leading-relaxed text-sm">
                Zertifizierte Stillberaterin (IBCLC) mit höchster fachlicher Qualifikation. Katrin
                steht für evidenzbasierte, individuelle Stillberatung und begleitet Frauen mit
                Erfahrung, Präzision und viel Feingefühl.
              </p>
            </div>

            <div className="flex flex-col items-center text-center bg-white rounded-2xl p-8 shadow-sm">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-5 text-3xl">
                👩‍⚕️
              </div>
              <h3 className="text-lg font-bold text-primary mb-1">Susanne Klinge</h3>
              <p className="text-sm font-semibold text-muted-foreground tracking-wide uppercase mb-3">
                Fachärztin für Gynäkologie
              </p>
              <p className="text-secondary leading-relaxed text-sm">
                Mit ihrer auf Stillberatung spezialisierten Praxis verbindet sie medizinische
                Expertise mit praktischer Stillbegleitung. Sie erkennt früh, worauf es ankommt – und
                schafft Lösungen, die im echten Leben funktionieren.
              </p>
            </div>

            <div className="flex flex-col items-center text-center bg-white rounded-2xl p-8 shadow-sm">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-5 text-3xl">
                👩‍🔬
              </div>
              <h3 className="text-lg font-bold text-primary mb-1">
                Prof. Dr. Sarah Kittel-Schneider
              </h3>
              <p className="text-sm font-semibold text-muted-foreground tracking-wide uppercase mb-3">
                Wissenschaft & Mentale Gesundheit
              </p>
              <p className="text-secondary leading-relaxed text-sm">
                Deutschlands Expertin für die Verbindung von Stillen und mentaler Gesundheit. Sie
                bringt wissenschaftliche Exzellenz und tiefes Verständnis für die psychischen
                Herausforderungen rund ums Stillen zusammen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Baby-Friendly Hospital Initiative */}
      <section className="py-20 bg-background overflow-hidden">
        <div className="max-w-6xl mx-auto px-6">
          <div className="rounded-3xl bg-white overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2">
              {/* Bild */}
              <div className="relative h-72 md:h-auto min-h-[420px]">
                <SecretAdminLogin>
                  <img
                    src="/images/mother_kreissaal.png"
                    alt="Mutter im Kreißsaal"
                    className="absolute inset-0 w-full h-full object-cover cursor-pointer"
                  />
                </SecretAdminLogin>
                {/* Gradient-Overlay rechts für weichen Übergang */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-white/10 hidden md:block pointer-events-none" />
              </div>

              {/* Text */}
              <div className="px-10 py-12 md:px-14 flex flex-col justify-center">
                <span className="text-xs font-semibold tracking-widest text-muted-foreground uppercase mb-3">
                  WHO & UNICEF
                </span>
                <h2 className="text-2xl md:text-3xl text-primary font-bold mb-8 leading-snug">
                  Baby-Friendly
                  <br />
                  Hospital Initiative
                </h2>

                <div className="flex flex-col gap-7">
                  <div className="flex gap-4 items-start">
                    <div className="w-8 h-8 shrink-0 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                      1
                    </div>
                    <p className="text-secondary leading-relaxed text-sm">
                      Ein internationales Programm zur Förderung des Stillens, ins Leben gerufen von
                      der Weltgesundheitsorganisation und UNICEF.
                    </p>
                  </div>
                  <div className="flex gap-4 items-start">
                    <div className="w-8 h-8 shrink-0 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                      2
                    </div>
                    <p className="text-secondary leading-relaxed text-sm">
                      Ziel ist es, Kliniken und Geburtsstationen zu unterstützen, optimale
                      Bedingungen für einen erfolgreichen Stillstart zu schaffen – basierend auf den
                      „10 Schritten zum erfolgreichen Stillen".
                    </p>
                  </div>
                  <div className="flex gap-4 items-start">
                    <div className="w-8 h-8 shrink-0 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                      3
                    </div>
                    <p className="text-secondary leading-relaxed text-sm">
                      Krankenhäuser, die diese Standards erfüllen, werden als „babyfreundlich"
                      ausgezeichnet – ein Qualitätsmerkmal für besonders stillfördernde Betreuung.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* App Store Links - Mobile */}
        <div className="mt-10 flex flex-col items-center gap-3 md:hidden">
          <p className="text-muted-foreground text-sm font-medium">Hol dir die App:</p>
          <div className="flex gap-3">
            <a
              href="https://play.google.com/store/apps/details?id=de.stillberatung.institut"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 rounded-lg text-white text-sm"
            >
              <Download className="w-4 h-4" />
              Google Play
            </a>
            <a
              href="https://apps.apple.com/app/institut-fuer-stillberatung/id0000000000"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 rounded-lg text-white text-sm"
            >
              <Download className="w-4 h-4" />
              App Store
            </a>
          </div>
        </div>
      </section>

      {/* Hebammen Partner */}
      {config.showMidwife && (
        <section className="relative h-screen overflow-hidden">
          <img
            src="https://sihubtivdmbmvkgjgpda.storage.eu-central-1.nhost.run/v1/files/fb6a9af3-c5cc-4134-92ac-f9cfdc9451d4"
            alt="Hebamme"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-background to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-secondary-foreground to-transparent" />
          <div className="relative h-full flex flex-col items-center justify-center max-w-4xl mx-auto text-center px-6">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-6">
              <Heart className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl text-white font-bold mb-4">Für Hebammen</h2>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
              Du bist Hebamme und möchtest deinen Frauen den bestmöglichen Start in die Stillzeit
              ermöglichen? Werde Teil unseres Netzwerks und profitiere von vorbereiteten,
              selbstbewussten Müttern.
            </p>
            <Button
              onClick={() => navigate('/partner')}
              className="px-8 py-3 bg-background hover:bg-white text-primary text-base rounded-full font-semibold shadow-md"
            >
              Kennenlernen
            </Button>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-secondary-foreground py-10">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <nav className="flex flex-wrap justify-center gap-6 text-sm text-primary">
              <a href="/impressum" className="hover:text-primary transition">
                Impressum
              </a>
              <a href="/datenschutz" className="hover:text-primary transition">
                Datenschutz
              </a>
              <a href="/agb" className="hover:text-primary transition">
                AGB
              </a>
              <a
                href="mailto:christine.lactara@gmail.com"
                className="hover:text-primary transition"
              >
                Kontakt
              </a>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  )
}
